/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { sendSignInLinkToEmail, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "../firebase";
import { COLOR, FONT, RADIUS, SHADOW } from "../utils/theme";

const COOLDOWN_MS = 60000;
const SHOW_RESEND_MS = 10000;

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [googleError, setGoogleError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const resendTimerRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(resendTimerRef.current);
      clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSend = async () => {
    setError("");
    if (!validateEmail(email)) {
      setError("Please enter a valid email");
      return;
    }
    if (cooldown) {
      setError("Too many requests — wait a moment");
      return;
    }

    setSending(true);
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.origin,
        handleCodeInApp: true,
      });
      localStorage.setItem("emailForSignIn", email);
      setSent(true);
      setShowResend(false);

      // Show "send again" after 10s
      resendTimerRef.current = setTimeout(() => setShowResend(true), SHOW_RESEND_MS);

      // Cooldown between sends
      setCooldown(true);
      cooldownTimerRef.current = setTimeout(() => setCooldown(false), COOLDOWN_MS);
    } catch (e) {
      console.error("[SignIn] sendSignInLinkToEmail failed:", e);
      if (e.code === "auth/too-many-requests") {
        setError("Too many requests — wait a moment");
      } else {
        setError("Something went wrong — try again");
      }
    } finally {
      setSending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleError("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") {
        console.error("[SignIn] Google sign-in failed:", e);
        setGoogleError("Google sign-in failed — try again");
      }
    }
  };

  const handleResend = () => {
    setSent(false);
    setShowResend(false);
    handleSend();
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: COLOR.cream,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        width: "100%",
        maxWidth: 400,
        background: COLOR.white,
        borderRadius: RADIUS.xl,
        boxShadow: SHADOW.panel,
        padding: 40,
        textAlign: "center",
      }}>
        {/* Heading */}
        <h1 style={{
          fontFamily: FONT.display,
          fontSize: 36,
          color: COLOR.inkDark,
          margin: "0 0 4px",
          fontWeight: 400,
        }}>
          Wanderologie
        </h1>
        <p style={{
          fontFamily: FONT.serif,
          fontSize: 14,
          color: COLOR.inkMid,
          margin: "0 0 32px",
        }}>
          Your AI-powered trip planner
        </p>

        {sent ? (
          /* Confirmation state */
          <div>
            <p style={{
              fontFamily: FONT.serif,
              fontSize: 15,
              color: COLOR.inkDark,
              lineHeight: 1.5,
              margin: "0 0 16px",
            }}>
              Check your email — we sent you a sign-in link
            </p>
            {showResend && (
              <button
                onClick={handleResend}
                disabled={cooldown}
                style={{
                  background: "none",
                  border: "none",
                  fontFamily: FONT.serif,
                  fontSize: 13,
                  color: cooldown ? COLOR.inkLight : COLOR.mauve,
                  cursor: cooldown ? "default" : "pointer",
                  textDecoration: "underline",
                  padding: 0,
                }}
              >
                Didn't get it? Send again
              </button>
            )}
          </div>
        ) : (
          /* Input state */
          <div>
            <button
              onClick={handleGoogleSignIn}
              style={{
                width: "100%",
                padding: "12px 0",
                background: COLOR.white,
                color: "#444",
                border: "1px solid #E0D5D0",
                borderRadius: RADIUS.md,
                fontFamily: FONT.serif,
                fontSize: 15,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#F5F5F5"}
              onMouseLeave={(e) => e.currentTarget.style.background = COLOR.white}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>

            {googleError && (
              <p style={{
                fontFamily: FONT.serif,
                fontSize: 13,
                color: COLOR.mauveDark,
                margin: "8px 0 0",
              }}>
                {googleError}
              </p>
            )}

            {/* Divider */}
            <div style={{
              display: "flex",
              alignItems: "center",
              margin: "20px 0",
              gap: 12,
            }}>
              <div style={{ flex: 1, height: 1, background: COLOR.borderWarm }} />
              <span style={{
                fontFamily: FONT.serif,
                fontSize: 13,
                color: COLOR.inkLight,
              }}>or</span>
              <div style={{ flex: 1, height: 1, background: COLOR.borderWarm }} />
            </div>

            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && !sending && handleSend()}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "12px 16px",
                fontFamily: FONT.serif,
                fontSize: 15,
                color: COLOR.inkDark,
                background: COLOR.cream,
                border: `1px solid ${COLOR.borderWarm}`,
                borderRadius: RADIUS.md,
                outline: "none",
                marginBottom: 12,
              }}
            />

            {error && (
              <p style={{
                fontFamily: FONT.serif,
                fontSize: 13,
                color: COLOR.mauveDark,
                margin: "0 0 12px",
              }}>
                {error}
              </p>
            )}

            <button
              onClick={handleSend}
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px 0",
                background: sending ? COLOR.mauveLight : COLOR.mauve,
                color: COLOR.white,
                border: "none",
                borderRadius: RADIUS.md,
                fontFamily: FONT.serif,
                fontSize: 15,
                cursor: sending ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                transition: "background 0.2s ease",
              }}
            >
              {sending && (
                <span style={{
                  width: 16, height: 16,
                  border: `2px solid ${COLOR.white}`,
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.8s linear infinite",
                }} />
              )}
              Send sign-in link
            </button>
          </div>
        )}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
